import React, { useState, useRef } from 'react';
import { CgProfile } from 'react-icons/cg';
import ReactDOM from 'react-dom';
import { IoClose } from 'react-icons/io5';
import { useEffect } from 'react';
import { HiOutlineTemplate } from 'react-icons/hi';
import {
  FiPlus,
  FiUploadCloud,
  FiFileText,
  FiX,
  FiTrash2,
  FiLoader,
  FiEye,
  FiMaximize,
  FiDownload,
  FiEdit,
  FiUsers,
  FiCalendar,
} from 'react-icons/fi';
import { renderAsync } from 'docx-preview';
import TextareaAutosize from 'react-textarea-autosize';

import {
  listTemplates,
  uploadTemplate,
  deleteTemplate,
  downloadTemplate,
  toggleTemplateStatus,
  API_URL,
  updateTemplate,
} from '../services/api';
import { DataState } from '../context/DataProvider';
import PreviewModal from './chat/PreviewModal';
import UserManagementContent from './modals/UserManagementContent';
import MeetingsContent from './modals/MeetingsContent';

const modalRoot = document.getElementById('modal-root');

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  headerActions,
  size = '2xl',
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  // Use ReactDOM.createPortal to render the modal outside of the current component hierarchy
  return ReactDOM.createPortal(
    // These classes are now applied to the viewport, guaranteeing centering
    // eslint-disable-next-line
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 backdrop-blur-sm transition-opacity"
      onClick={onClose}
    >
      {/* eslint-disable-next-line */}
      <div
        className={`text-p-text relative flex max-h-[90vh] w-full flex-col rounded-2xl bg-zinc-900 shadow-xl max-w-${size}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* --- CHANGES ARE HERE --- */}
        <div className="flex items-center justify-between border-b border-slate-700 p-4 md:p-6">
          <h3 className="text-xl font-semibold text-white">{title}</h3>

          {/* Container for all buttons on the right */}
          <div className="flex items-center gap-4">
            {/* 1. Render the custom action buttons passed via props */}
            {headerActions}

            {/* 2. The original close button */}
            <button
              onClick={onClose}
              className="rounded-full p-1 text-gray-400 transition-colors hover:bg-slate-700 hover:text-white"
            >
              <IoClose size={24} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="overflow-y-auto p-4 md:p-6">{children}</div>
      </div>
    </div>,
    modalRoot, // The target DOM node for the portal
  );
};

const ProfileContent = ({ userInfo }) => (
  <div>
    <h2 className="mb-6 text-lg font-semibold text-slate-100">
      Profile Information
    </h2>
    <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
      {userInfo.picture ? (
        <img
          src={userInfo.picture}
          alt={userInfo.name}
          className="h-20 w-20 flex-shrink-0 rounded-full border-4 border-slate-700 object-cover"
        />
      ) : (
        <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-full border-4 border-slate-700 bg-gray-300 text-4xl font-bold text-gray-600">
          {userInfo.name
            ? userInfo.name?.charAt(0).toUpperCase()
            : userInfo.email?.charAt(0).toUpperCase()}
        </div>
      )}
      <div className="mt-1 flex flex-col gap-4 text-center sm:text-left">
        <div>
          <p className="text-xs text-slate-400">Name</p>
          <p className="text-sm text-slate-100">{userInfo.name}</p>
        </div>
        <div>
          <p className="text-xs text-slate-400">Email</p>
          <p className="break-all text-sm text-slate-100">{userInfo.email}</p>
        </div>
      </div>
    </div>
  </div>
);

const EditTemplateModal = ({ isOpen, onClose, template, onSave }) => {
  const [name, setName] = useState(template.template_name);
  const [useCase, setUseCase] = useState(template.template_use_case);
  const [instructions, setInstructions] = useState(template.instructions || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveChanges = async () => {
    setIsSaving(true);
    const updateData = {
      template_name: name,
      template_use_case: useCase,
      instructions: instructions,
    };
    await onSave(template._id, updateData);
    setIsSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Template">
      <div className="space-y-4">
        <div>
          <label
            htmlFor="templateName"
            className="text-xs font-medium text-slate-400"
          >
            Template Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 p-2 text-sm text-slate-200 outline-none focus:border-[#5652CF] focus:ring-2 focus:ring-[#5652CF]"
          />
        </div>
        <div>
          <label
            htmlFor="useCase"
            className="text-xs font-medium text-slate-400"
          >
            When to use this template
          </label>
          <textarea
            id="useCase"
            value={useCase}
            onChange={(e) => setUseCase(e.target.value)}
            rows="3"
            className="mt-1 w-full resize-none rounded-md border border-slate-700 bg-slate-900 p-2 text-sm text-slate-200 outline-none focus:border-[#5652CF] focus:ring-2 focus:ring-[#5652CF]"
          />
        </div>
        <div>
          <label
            htmlFor="instructions"
            className="text-xs font-medium text-slate-400"
          >
            Instructions for AI Agent
          </label>
          {/* --- CHANGE IS HERE --- */}
          {/* 1. Replace <textarea> with <TextareaAutosize> */}
          {/* 2. Remove rows="4" */}
          {/* 3. Add minRows and maxRows to control the dynamic height */}
          <TextareaAutosize
            minRows={4} // The textarea will start at this height
            maxRows={10} // It will grow up to this height, then scroll
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="e.g., Keep table headers constant. Replace content in {{placeholder}}. Summarize the key metrics section. Update the date in the footer."
            className="mt-1 w-full resize-none rounded-md border border-slate-700 bg-slate-900 p-2 text-sm text-slate-200 outline-none focus:border-[#5652CF] focus:ring-2 focus:ring-[#5652CF]"
          />
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <button
            onClick={onClose}
            className="rounded-md px-4 py-2 text-xs text-slate-300 transition-colors hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveChanges}
            disabled={isSaving}
            className="flex h-8 w-32 items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-600"
          >
            {isSaving ? <FiLoader className="animate-spin" /> : 'Save Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// --- TemplatesContent is now a fully interactive component ---
const TemplatesContent = ({ accessToken }) => {
  // 2. State to manage the list of templates
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdding, setIsAdding] = useState(false); // To toggle the add form

  // State for the new template form
  const [newTemplateFile, setNewTemplateFile] = useState(null);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateUseCase, setNewTemplateUseCase] = useState('');
  const [newTemplateInstructions, setNewTemplateInstructions] = useState(''); // +++ State for new instructions
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // +++ State for managing the edit modal +++
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Rename `deletingId` to be more generic, as it now handles both delete and toggle loading states
  const [processingId, setProcessingId] = useState(null);

  // --- State for the preview system ---
  // For the large preview modal
  const [previewFile, setPreviewFile] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  // +++ 1. Ref and loading state for the NEW small preview +++
  const smallPreviewRef = useRef(null);
  const [isSmallPreviewLoading, setIsSmallPreviewLoading] = useState(false);

  // +++ 2. useEffect to render the SMALL preview when a new file is added +++
  useEffect(() => {
    if (isAdding && newTemplateFile && smallPreviewRef.current) {
      setIsSmallPreviewLoading(true);
      smallPreviewRef.current.innerHTML = ''; // Clear previous preview
      const reader = new FileReader();
      reader.onload = function (event) {
        const arrayBuffer = event.target.result;
        renderAsync(arrayBuffer, smallPreviewRef.current, null, {
          inWrapper: true,
          ignoreHeight: false,
        })
          .then(() => setIsSmallPreviewLoading(false))
          .catch((err) => {
            setIsSmallPreviewLoading(false);
          });
      };
      reader.readAsArrayBuffer(newTemplateFile);
    }
  }, [isAdding, newTemplateFile]); // Reruns when a new file is selected

  // --- DATA FETCHING ---
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        // Replace accessToken with your actual accessToken from context
        const fetchedTemplates = await listTemplates(accessToken);
        setTemplates(fetchedTemplates);
      } catch (err) {
        setError(err.message || 'Failed to fetch templates.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Runs once on mount

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith('.docx')) {
      setNewTemplateFile(file);
    } else {
      alert('Please upload a .docx file only.');
    }
  };

  // --- HANDLER FUNCTIONS (with updated loading logic) ---
  const handleSaveTemplate = async () => {
    if (!newTemplateFile || !newTemplateName || !newTemplateUseCase) {
      alert('Please complete all fields.');
      return;
    }
    // --- Start loading state ---
    setIsUploading(true);
    try {
      const response = await uploadTemplate(
        accessToken,
        newTemplateName,
        newTemplateUseCase,
        newTemplateFile,
        newTemplateInstructions,
      );
      // Add the file object to the response so it can be previewed immediately
      response.template_info.file = newTemplateFile;
      setTemplates([response.template_info, ...templates]);
      handleCancel();
    } catch (err) {
      alert(`Upload failed: ${err.message || 'Please try again.'}`);
    } finally {
      // --- End loading state ---
      setIsUploading(false);
    }
  };

  const handleEditClick = (template) => {
    setEditingTemplate(template);
    setIsEditModalOpen(true);
  };
  const handleUpdateTemplate = async (templateId, updateData) => {
    try {
      await updateTemplate(accessToken, templateId, updateData);
      // Update the template in the local state for instant UI feedback
      setTemplates((prev) =>
        prev.map((t) => (t._id === templateId ? { ...t, ...updateData } : t)),
      );
      setIsEditModalOpen(false); // Close modal on success
    } catch (err) {
      alert(`Update failed: ${err.message || 'Please try again.'}`);
    }
  };

  const handleDownloadClick = async (template) => {
    setProcessingId(template._id); // Show loading feedback on the item
    try {
      const { url } = await downloadTemplate(accessToken, template._id);
      // Create a temporary link to trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', template.filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert(`Download failed: ${err.message || 'Please try again.'}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleToggleStatus = async (templateId, currentStatus) => {
    setProcessingId(templateId);

    // Optimistic UI update for instant feedback
    setTemplates((prev) =>
      prev.map((t) =>
        t._id === templateId ? { ...t, is_active: !currentStatus } : t,
      ),
    );

    try {
      await toggleTemplateStatus(accessToken, templateId, !currentStatus);
    } catch (err) {
      // Revert on failure
      setTemplates((prev) =>
        prev.map((t) =>
          t._id === templateId ? { ...t, is_active: currentStatus } : t,
        ),
      );
      alert(`Failed to update status: ${err.message || 'Please try again.'}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?'))
      return;

    // +++ 2. Set the ID of the item we are currently deleting +++
    setProcessingId(templateId);

    try {
      await deleteTemplate(accessToken, templateId);
      // If successful, remove the item from the list
      setTemplates((prevTemplates) =>
        prevTemplates.filter((t) => t._id !== templateId),
      );
    } catch (err) {
      alert(`Deletion failed: ${err.message || 'Please try again.'}`);
    } finally {
      // +++ 3. Clear the deleting ID regardless of success or failure +++
      setProcessingId(null);
    }
  };

  const handlePreviewClick = async (template) => {
    // If the file object already exists (from a recent upload), use it.
    if (template.file) {
      setPreviewFile(template.file);
      setIsPreviewOpen(true);
      return;
    }
    setProcessingId(template._id); // Show loading feedback on the item
    try {
      const response = await fetch(
        `${API_URL}/template/download-direct/${template._id}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        throw new Error('Could not download file for preview.');
      }

      const blob = await response.blob();
      const file = new File([blob], template.filename || 'template.docx', {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      setPreviewFile(file);
      setIsPreviewOpen(true);
    } catch (err) {
      alert(`Could not load preview: ${err.message || 'Please try again.'}`);
    } finally {
      setProcessingId(null); // Clear the processing ID regardless of success or failure
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setNewTemplateFile(null);
    setNewTemplateName('');
    setNewTemplateUseCase('');
  };

  const handleLargePreviewClick = () => {
    if (newTemplateFile) {
      setPreviewFile(newTemplateFile);
      setIsPreviewOpen(true);
    }
  };

  // --- RENDER LOGIC ---
  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <FiLoader className="animate-spin text-3xl text-[#5652CF]" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-red-500">{error}</div>;
  }

  return (
    <div>
      {/* Render the new Edit Modal when a template is being edited */}
      {editingTemplate && (
        <EditTemplateModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          template={editingTemplate}
          onSave={handleUpdateTemplate}
        />
      )}
      {/* +++ Render the Preview Modal +++ */}
      <PreviewModal
        file={previewFile}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-100">My Templates</h2>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-700"
          >
            <FiPlus />
            Add New
          </button>
        )}
      </div>

      {/* 4. The "Add New Template" Form */}
      {isAdding && (
        <div className="mb-6 space-y-4 rounded-lg border border-slate-700 bg-slate-800 p-4">
          {!newTemplateFile ? (
            <div>
              <p className="mb-2 text-sm font-medium text-slate-200">
                Step 1: Upload a .docx file
              </p>
              <input
                type="file"
                accept=".docx"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
              />

              <button
                onClick={() => fileInputRef.current.click()}
                className="block cursor-pointer rounded-lg border-2 border-dashed border-slate-600 p-6 text-center transition-colors hover:border-[#5652CF] hover:bg-slate-700/50"
              >
                <FiUploadCloud className="mx-auto mb-2 text-3xl text-slate-400" />
                <p className="text-sm text-slate-300">
                  Click to browse or drag & drop
                </p>
                <p className="mt-1 text-xs text-slate-500">DOCX files only</p>
              </button>
            </div>
          ) : (
            <div>
              <p className="mb-2 text-sm font-medium text-slate-200">
                Step 2: Describe your template
              </p>
              <div className="flex items-center justify-between rounded-md bg-slate-700 p-2">
                <div className="flex items-center gap-2">
                  <FiFileText className="text-blue-400" />
                  <span className="text-xs text-slate-300">
                    {newTemplateFile.name}
                  </span>
                </div>
                <button
                  onClick={() => setNewTemplateFile(null)}
                  className="text-slate-400 hover:text-white"
                >
                  <FiX />
                </button>
              </div>
            </div>
          )}

          {/* +++ 3. Small Preview Area - This is the new UI section +++ */}
          {/* +++ 3. Small Preview Area - With scaling for a "zoomed-out" view +++ */}
          {newTemplateFile && (
            <div>
              <label
                htmlFor="template-preview"
                className="text-xs font-medium text-slate-400"
              >
                Template Preview
              </label>
              {/* eslint-disable-next-line */}
              <div
                onClick={handleLargePreviewClick}
                // The outer container still controls the size and clips the content
                className="group relative mt-1 h-56 cursor-pointer overflow-hidden rounded-lg border-2 border-slate-600 bg-slate-100 brightness-50"
              >
                {isSmallPreviewLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
                    <FiLoader className="animate-spin text-3xl text-blue-600" />
                  </div>
                )}

                {/* This div is the mount point for the small preview.
              We now add an inline style to scale down its content.
            */}
                <div
                  ref={smallPreviewRef}
                  style={{
                    // --- CHANGES ARE HERE ---
                    // 1. Zoom out the content to 25% of its original size.
                    //    This makes more of the document visible in the small preview area.
                    transform: 'scale(0.50)',

                    // 2. Set the origin point for the scaling to the top-left corner.
                    //    This is crucial to ensure we see the start of the document.
                    transformOrigin: 'center top',
                  }}
                ></div>

                {/* "Click to expand" overlay (unchanged) */}
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/50 text-white opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
                  <FiMaximize size={24} />
                  <span className="text-xs font-semibold">Click to expand</span>
                </div>
              </div>
            </div>
          )}

          {newTemplateFile && (
            <>
              <div>
                <label
                  htmlFor="template-name"
                  className="text-xs font-medium text-slate-400"
                >
                  Template Name
                </label>
                <input
                  type="text"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="e.g., Weekly Project Report"
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 p-2 text-sm text-slate-200 outline-none focus:border-[#5652CF] focus:ring-2 focus:ring-[#5652CF]"
                />
              </div>
              <div>
                <label
                  htmlFor="template-use-case"
                  className="text-xs font-medium text-slate-400"
                >
                  Use Case
                </label>
                <textarea
                  value={newTemplateUseCase}
                  onChange={(e) => setNewTemplateUseCase(e.target.value)}
                  rows="3"
                  placeholder="e.g., For summarizing weekly progress to stakeholders."
                  className="mt-1 w-full resize-none rounded-md border border-slate-700 bg-slate-900 p-2 text-sm text-slate-200 outline-none focus:border-[#5652CF] focus:ring-2 focus:ring-[#5652CF]"
                />
              </div>
              <div>
                <label
                  htmlFor="intructions-for-ai"
                  className="text-xs font-medium text-slate-400"
                >
                  Instructions for AI Agent
                </label>
                {/* --- CHANGE IS HERE --- */}
                <TextareaAutosize
                  minRows={4}
                  maxRows={10}
                  value={newTemplateInstructions}
                  onChange={(e) => setNewTemplateInstructions(e.target.value)}
                  placeholder="e.g., Keep table headers constant. Replace content inside {{placeholders}}. Summarize the key metrics section. Update the date in the footer."
                  className="mt-1 w-full resize-none rounded-md border border-slate-700 bg-slate-900 p-2 text-sm text-slate-200 outline-none focus:border-[#5652CF] focus:ring-2 focus:ring-[#5652CF]"
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={handleCancel}
              className="rounded-md px-4 py-2 text-xs text-slate-300 transition-colors hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveTemplate}
              // +++ Disable button when required fields are missing OR when uploading +++
              disabled={
                !newTemplateFile ||
                !newTemplateName ||
                !newTemplateUseCase ||
                isUploading
              }
              className="rounded-md bg-blue-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-600"
            >
              Save Template
            </button>
          </div>
        </div>
      )}

      {/* List of Existing Templates */}
      <div className="space-y-3">
        {templates.length > 0 ? (
          templates.map((template, index) => {
            // +++ 4. Check if this specific item is being deleted +++
            const isProcessingThis = processingId === template._id;

            return (
              <div
                key={index}
                className="group rounded-lg border border-slate-700/60 bg-slate-800/80 p-3"
              >
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-sm font-semibold text-blue-400">
                    {template.template_name}
                  </h3>

                  {/* --- CONTROLS --- */}
                  <div className="flex flex-shrink-0 items-center gap-4">
                    {/* --- Toggle Switch --- */}
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-medium ${template.is_active ? 'text-green-400' : 'text-slate-500'}`}
                      >
                        {isProcessingThis
                          ? '...'
                          : template.is_active
                            ? 'Active'
                            : 'Inactive'}
                      </span>
                      <button
                        onClick={() =>
                          handleToggleStatus(template._id, template.is_active)
                        }
                        disabled={isProcessingThis}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:cursor-not-allowed ${template.is_active ? 'bg-green-500' : 'bg-slate-600'}`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${template.is_active ? 'translate-x-5' : 'translate-x-1'}`}
                        />
                      </button>
                    </div>

                    {/* --- Action Buttons --- */}
                    <div className="flex items-center gap-3 opacity-0 transition-opacity group-hover:opacity-100">
                      <button
                        onClick={() => handleEditClick(template)}
                        title="Edit"
                        className="text-slate-400 hover:text-white disabled:cursor-wait"
                        disabled={isProcessingThis}
                      >
                        <FiEdit size={14} />
                      </button>
                      <button
                        onClick={() => handleDownloadClick(template)}
                        disabled={isProcessingThis}
                        title="Download"
                        className="text-slate-400 hover:text-white disabled:cursor-wait"
                      >
                        {isProcessingThis ? (
                          <FiLoader className="animate-spin" size={14} />
                        ) : (
                          <FiDownload size={14} />
                        )}
                      </button>
                      <button
                        onClick={() => handlePreviewClick(template)}
                        disabled={isProcessingThis}
                        title="Preview"
                        className="text-slate-400 hover:text-blue-400 disabled:cursor-wait"
                      >
                        <FiEye size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template._id)}
                        disabled={isProcessingThis}
                        title="Delete"
                        className="text-slate-400 hover:text-red-500 disabled:cursor-wait"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
                <p className="mt-1 pr-4 text-xs text-slate-300">
                  {template.template_use_case}
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  Filename: {template.filename}
                </p>
              </div>
            );
          })
        ) : (
          <div className="py-8 text-center">
            <p className="text-sm text-slate-400">No templates found.</p>
            <p className="mt-1 text-xs text-slate-500">
              Click "Add New" to get started.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const ProfileSettingsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const { userInfo, userGoogleId: accessToken } = DataState();

  React.useEffect(() => {
    if (isOpen) {
      setActiveTab('profile');
    }
  }, [isOpen]);

  const displayUser = userInfo || {
    name: 'Sarthak Chaudhary',
    email: 'centinellegends124@gmail.com',
    picture: 'http://googleusercontent.com/file_content/1',
  };

  return (
    <Modal
      title="Account Settings"
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
    >
      {/* The main flex container no longer needs a min-height */}
      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        {/* Left Pane: Navigation - Stays fixed, does not scroll */}
        <div className="w-full flex-shrink-0 border-b border-slate-700/60 pb-4 md:w-48 md:border-b-0 md:border-r md:pb-0 md:pr-6">
          <nav className="flex flex-row gap-2 md:flex-col">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-xs font-medium transition-all duration-200 ${
                activeTab === 'profile'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
              }`}
            >
              <CgProfile className="flex-shrink-0 text-lg" />
              <span>Profile</span>
            </button>

            <button
              onClick={() => setActiveTab('templates')}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-xs font-medium transition-all duration-200 ${
                activeTab === 'templates'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
              }`}
            >
              <HiOutlineTemplate className="flex-shrink-0 text-lg" />
              <span>Templates</span>
            </button>

            {/* --- 3. ADD the new button for User Management --- */}
            <button
              onClick={() => setActiveTab('users')}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-xs font-medium transition-all duration-200 ${
                activeTab === 'users'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
              }`}
            >
              <FiUsers className="flex-shrink-0 text-lg" />
              <span>User Management</span>
            </button>
            {/* +++ 2. ADD THE NEW MEETINGS BUTTON +++ */}
            <button
              onClick={() => setActiveTab('meetings')}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-xs font-medium transition-all duration-200 ${
                activeTab === 'meetings'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
              }`}
            >
              <FiCalendar className="flex-shrink-0 text-lg" />
              <span>Meetings</span>
            </button>
          </nav>
        </div>

        {/* Right Pane: Content - This container is now the key.
                  - h-[400px]: Sets a fixed height.
                  - overflow-y-auto: Enables vertical scrolling ONLY when content is taller than 400px.
                  - pr-2: Adds padding on the right to make space for the scrollbar.
                */}
        <div className="custom-scrollbar mt-2 h-[350px] w-full overflow-y-auto pr-2 md:mt-0">
          {activeTab === 'profile' && <ProfileContent userInfo={displayUser} />}
          {activeTab === 'templates' && (
            <TemplatesContent accessToken={accessToken} />
          )}
          {/* --- 3. RENDER the new content component when the tab is active --- */}
          {activeTab === 'users' && <UserManagementContent />}
          {/* +++ 4. RENDER THE NEW MEETINGS CONTENT +++ */}
          {activeTab === 'meetings' && (
            <MeetingsContent accessToken={accessToken} />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ProfileSettingsModal;
